import { useEffect } from "react";
import { Modal, Form, Input, InputNumber, Row, Col } from "antd";
import { useTranslation } from "react-i18next";
import MenuImageUploader from "./MenuImageUploader"; // adjust import as needed

export default function MenuItemForm({
  visible,
  onClose,
  onSubmit,
  initialValues,
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  // Whenever modal opens with an item, populate the form
  useEffect(() => {
    if (visible && initialValues) {
      form.setFieldsValue({
        ...initialValues,
        section: {
          taxPercentage: initialValues.tax_percent,
          price: initialValues.price_inc_tax,
          weight: initialValues.weight,
          urn: initialValues.urn,
        },
      });
    } else {
      form.resetFields();
    }
  }, [visible, initialValues, form]);

  const handleOk = () => {
    form.validateFields().then((values) => {
      const formattedValues = {
        ...values,
        tax_percent: values.section?.taxPercentage,
        price_inc_tax: values.section?.price,
        weight: values.section?.weight,
        urn: values.section?.urn || initialValues?.urn,
      };
      delete formattedValues.section;

      onSubmit(formattedValues);
      form.resetFields();
    });
  };

  return (
    <Modal
      open={visible}
      title={initialValues ? t("edit_menu_item") : t("add_new_menu_item")}
      onCancel={onClose}
      onOk={handleOk}
      okText={initialValues ? t("update") : t("add_item")}
      bodyStyle={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 8 }}
    >
      <Form form={form} layout="vertical">
        {/* hidden imageId field */}
        <Form.Item name="imageId" hidden>
          <Input type="hidden" />
        </Form.Item>

        {/* uploader */}
        <Form.Item label={t("image")}>
          <MenuImageUploader
            form={form}
            initialImage={{
              id: initialValues?.imageId,
              url: initialValues?.imagePath,
            }}
          />
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="sku" label={t("sku")} rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="name"
              label={t("name")}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="title"
              label={t("title")}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="subtitle" label={t("subtitle")}>
              <Input />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="description"
              label={t("description")}
              rules={[{ required: true }]}
            >
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="usage"
              label={t("usage")}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="terms"
              label={t("terms")}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              label={t("tax_percentage")}
              name={["section", "taxPercentage"]}
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                max={1}
                step={0.01}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t("price")}
              name={["section", "price"]}
              rules={[{ required: true }]}
            >
              <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label={t("weight")}
              name={["section", "weight"]}
              rules={[{ required: true }]}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
